import {
  ActionPlanIntervention,
  DtoEnvelopeActionPlanIntervention,
  InterventionStatus,
} from '@/api/generated-catch-backend/model';
import ModalInterventionDetails from '@/app/components/action-plan/components/modals/details/modal-intervention-details';
import { Intervention } from '@/app/components/action-plan/types';
import icons from '@/assets/icons';
import { ROUTE_ACTION_PLAN, ROUTE_BUILD_ACTION_PLAN } from '@/constants/routes';
import useAnalyticsCalls, { DashboardSource } from '@/hooks/use-analytics';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecommendedActionCard } from './components/recommended-action-card';
import { convertToLocalIntervention } from './utils/interventionMapper';

interface RecommendedActionsProps {
  recommendedActions?: DtoEnvelopeActionPlanIntervention;
}

// Helper function to validate if an intervention has essential data for display
export const interventionHasEssentialData = (intervention: ActionPlanIntervention): boolean => {
  return !!(intervention.title && intervention.title.trim() !== '' && intervention.id && intervention.category);
};

export default function RecommendedActions({ recommendedActions }: RecommendedActionsProps) {
  const navigate = useNavigate();
  const { setNavigationSource } = useAnalyticsCalls();
  const [selectedIntervention, setSelectedIntervention] = useState<Intervention | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Show skeleton loader while data is loading
  if (!recommendedActions) {
    return (
      <div className="risk-cards-container">
        <div className="flex flex-col items-center gap-5">
          <div className="w-full">
            <div className="flex flex-col gap-2">
              <div className="h-7 w-48 animate-pulse rounded-full bg-gray-300" />
              <div className="h-5 w-3/4 animate-pulse rounded-full bg-gray-200" />
              <div className="h-3 w-2/3 animate-pulse rounded-full bg-gray-200" />
            </div>
          </div>
          <div className="w-full">
            <div className="flex w-full flex-col gap-3 pt-2 pb-2">
              {[1, 2, 3].map((index) => (
                <div
                  key={`skeleton-${index}`}
                  className="relative h-[63px] w-[295px] rounded-[24px] border border-[#ededed] bg-white p-3 md:w-[744px]"
                >
                  <div className="flex items-center gap-3">
                    {/* Skeleton icon on the left - vertically centered */}
                    <div className="h-10 w-10 flex-shrink-0 animate-pulse rounded-lg bg-gray-200" />

                    {/* Skeleton content in the middle */}
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-3/4 animate-pulse rounded bg-gray-300" />
                      <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
                    </div>

                    {/* Skeleton arrow on the right - vertically centered */}
                    <div className="h-10 w-10 flex-shrink-0 animate-pulse rounded-full bg-gray-200" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full max-w-[335px]">
            <div className="h-10 w-full animate-pulse rounded-full bg-gray-300" />
          </div>
        </div>
      </div>
    );
  }

  // Partitioning data into multiple groups (IN_PROGRESS and AVAILABLE) in one pass
  const { inProgressActions, availableActions } = recommendedActions.data.reduce(
    (acc, intervention) => {
      // Only include interventions that have essential data
      if (!interventionHasEssentialData(intervention)) {
        return acc;
      }

      if (intervention.status === InterventionStatus.IN_PROGRESS) {
        acc.inProgressActions.push(intervention);
      } else if (intervention.status === InterventionStatus.AVAILABLE) {
        acc.availableActions.push(intervention);
      }
      return acc;
    },
    { inProgressActions: [] as ActionPlanIntervention[], availableActions: [] as ActionPlanIntervention[] },
  );
  const hasActiveActionPlan = inProgressActions.length > 0;
  const hasActions = inProgressActions.length > 0 || availableActions.length > 0;
  const topActions = hasActiveActionPlan
    ? inProgressActions.slice(0, 3)
    : hasActions
      ? availableActions.slice(0, 3)
      : ([] as ActionPlanIntervention[]);

  // Helper function to render action icon
  const renderActionIcon = (action: ActionPlanIntervention) => {
    if (action.iconAsset?.url) {
      return <img src={action.iconAsset.url} alt={action.iconAsset.alt || action.title} className="h-auto w-[36px]" />;
    }

    // Default to biomarkers-alt-1 icon when no specific icon is available
    const iconSrc = icons['biomarkers-alt-1'];
    return <img src={iconSrc} alt={action.title} className="h-auto w-[36px]" />;
  };

  return (
    <div className="risk-cards-container">
      <h2 className="mobile-h3-primary text-primary action-item-text-width text-left font-bold">
        {hasActiveActionPlan ? 'Your Action Plan' : 'Your Recommended Actions'}
      </h2>
      <AnimatePresence mode="wait">
        <motion.p
          className="body-regular text-primary action-item-text-width text-left"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {hasActiveActionPlan
            ? 'Complete these actions to reduce your risk.'
            : hasActions
              ? 'These are the top changes we recommend to reduce your risk.'
              : 'You’ve completed all of your actions.'}
        </motion.p>
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div
          className="flex flex-col items-center gap-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className="flex w-full flex-col gap-3 pt-2 pb-2">
            {topActions.map((action) => (
              <RecommendedActionCard
                key={action.id}
                onClick={() => {
                  setNavigationSource(DashboardSource.DASHBOARD_SPECIFIC_ACTION);
                  setSelectedIntervention(convertToLocalIntervention(action));
                  setIsModalOpen(true);
                }}
                action={action}
              />
            ))}
          </div>

          {hasActiveActionPlan ? (
            <div className="cta-button-container">
              <button
                onClick={() => {
                  setNavigationSource(DashboardSource.DASHBOARD_ACTION_CTA);
                  navigate(ROUTE_ACTION_PLAN);
                }}
                className="cta-button body-meta"
              >
                See Your Action Plan
              </button>
            </div>
          ) : (
            <div className="cta-button-container">
              <button
                onClick={() => {
                  setNavigationSource(DashboardSource.DASHBOARD_ACTION_CTA);
                  navigate(ROUTE_BUILD_ACTION_PLAN);
                }}
                className="cta-button body-meta"
              >
                See Your Recommended Actions
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {selectedIntervention && (
        <ModalInterventionDetails
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedIntervention(null);
          }}
          intervention={selectedIntervention}
          navToActionPlanHome={hasActiveActionPlan}
        />
      )}
    </div>
  );
}
